import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import {
  AccountingTypeFormSchema,
  AccountingTypeModel,
  AccountingTypesModel,
  type UpdateAccountingTypeSortOrderParamsSchema,
  type accountingType,
} from '../models';
import { accountTypeNameWithCode, queryKeyMap } from '../utils';

const AccountingTypesModelElement = AccountingTypesModel.element;

export type AccountingTypesParams = {
  referenceFiscalYear?: string;
  showMigrated?: true;
};

export const useAccountingTypes = <
  T = Array<z.infer<typeof AccountingTypesModelElement>>,
>({
  params,
  select,
  enabled,
}: {
  params?: AccountingTypesParams;
  select?: (data: z.infer<typeof AccountingTypesModel>) => T;
  enabled?: boolean;
} = {}) => {
  const { showMigrated, ...restParams } = params ?? {};
  return useQuery({
    queryKey: queryKeyMap.accountingTypes(params),
    queryFn: async () => {
      const accountingTypes = await axiosGet('/api/accounting-types/', {
        ...restParams,
        ...(!showMigrated && { isMigrated: false }),
      });
      return AccountingTypesModel.parse(accountingTypes);
    },
    select,
    enabled,
  });
};

export const useAccountingTypeOptions = ({
  type,
  showCode,
  params,
  enabled,
}: {
  type?: z.infer<typeof accountingType>;
  showCode?: boolean;
  params?: { referenceFiscalYear?: string; showMigrated?: true };
  enabled?: boolean;
} = {}) => {
  return useAccountingTypes({
    params,
    select: (data) =>
      data.flatMap((d) =>
        !type || d.type === type
          ? {
              value: d.id,
              label: showCode
                ? accountTypeNameWithCode(d.code, d.name)
                : d.name,
            }
          : [],
      ),
    enabled,
  });
};

export const useAccountingType = <T = z.infer<typeof AccountingTypeModel>>({
  id,
  select,
}: {
  id: string;
  select?: (data: z.infer<typeof AccountingTypeModel>) => T;
}) => {
  return useQuery({
    queryKey: ['accountingType', id],
    queryFn: async () => {
      const accountingType = await axiosGet(`/api/accounting-types/${id}/`);
      return AccountingTypeModel.parse(accountingType);
    },
    select,
    enabled: !!id,
  });
};

type FormValues = z.infer<typeof AccountingTypeFormSchema>;

export const useCreateAccountingType = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation(
    {
      mutationFn: async (values: FormValues) => {
        const { period, ...payload } = AccountingTypeFormSchema.parse(values);
        return await axiosPost('/api/accounting-types/', {
          ...payload,
          startYear: period?.[0]?.year() ?? null,
          endYear: period?.[1]?.year() ?? null,
        });
      },
      onSuccess: async () => {
        await invalidate(queryKeyMap.accountingTypes());
      },
    },
    true,
  );
};

export const useUpdateAccountingType = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation(
    {
      mutationFn: async (values: FormValues & { id: string }) => {
        const { id, period, ...payload } = AccountingTypeFormSchema.extend({
          id: z.string().uuid(),
        }).parse(values);
        return await axiosPatch(`/api/accounting-types/${id}/`, {
          ...payload,
          startYear: period?.[0]?.year() ?? null,
          endYear: period?.[1]?.year() ?? null,
        });
      },
      onSuccess: async () => {
        await invalidate(queryKeyMap.accountingTypes());
      },
    },

    true,
  );
};

export const useDeleteAccountingType = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`/api/accounting-types/${id}/`);
      return res;
    },
    onSuccess: async () => {
      await invalidate(queryKeyMap.accountingTypes());
    },
  });
};

export const useChangeOrderOfAccountingType = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      params: z.infer<typeof UpdateAccountingTypeSortOrderParamsSchema>,
    ) => {
      const res = await axiosPost(
        '/api/accounting-types/update-sort-order/',
        params,
      );
      // NOTE:
      // invalidate query to update the order of accounting types.
      // Only the order is updated, so the accountingType is not updated.
      await invalidate(['accountingTypes']);
      return res;
    },
  });
};
